import { Text, VStack } from '@chakra-ui/react';
import { Plural, Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';

// Confirm-before-spend for forced re-translation, at two weights: one cell, or
// a whole course/unit. Not the destructive variant: nothing is lost (the
// current translation stays served until the new one lands); a re-run just
// costs money, so the dialog states the size before the click.

function SharedNotes() {
  return (
    <>
      <Text>
        <Trans>Human edits are kept and re-applied automatically.</Trans>
      </Text>
      <Text>
        <Trans>The current translation stays live until the new one is ready.</Trans>
      </Text>
    </>
  );
}

export function RetranslateCellDialog({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  languageName,
  segmentCount,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  languageName: string;
  segmentCount: number | null;
}) {
  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      isLoading={isLoading}
      title={t`Re-translate ${languageName}?`}
      confirmLabel={t`Re-translate`}
      body={
        <VStack align="flex-start" spacing={2} fontSize="sm">
          <Text>
            {segmentCount != null && segmentCount > 0 ? (
              <Trans>
                Re-runs this translation (~{segmentCount} segments) through the current engine.
              </Trans>
            ) : (
              <Trans>Re-runs this translation through the current engine.</Trans>
            )}
          </Text>
          <SharedNotes />
        </VStack>
      }
    />
  );
}

export type RetranslateScopeEstimate = {
  translations: number;
  resources: number;
  languages: number;
  segments: number;
};

export function RetranslateScopeDialog({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  title,
  estimate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  title: string;
  estimate: RetranslateScopeEstimate | undefined;
}) {
  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      isLoading={isLoading}
      title={title}
      confirmLabel={
        estimate == null ? (
          t`Re-translate`
        ) : (
          <Plural
            value={estimate.translations}
            one={`Re-translate # translation`}
            other={`Re-translate # translations`}
          />
        )
      }
      body={
        <VStack align="flex-start" spacing={2} fontSize="sm">
          {estimate == null ? (
            <Text color="fg.muted">
              <Trans>Working out what this covers…</Trans>
            </Text>
          ) : (
            <Text>
              <Trans>
                Re-runs {estimate.translations} translations across {estimate.languages} languages
                and {estimate.resources} resources through the current engine
                {estimate.segments > 0 ? ` (~${estimate.segments} segments)` : ''}.
              </Trans>
            </Text>
          )}
          <SharedNotes />
        </VStack>
      }
    />
  );
}
